import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Camera,
  FileSpreadsheet,
  Mic,
  Type,
  Zap,
  CheckCircle,
  ArrowRight,
  Receipt,
  PieChart,
  Smartphone,
  Monitor,
  AlertTriangle,
  HandCoins,
  LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    {
      icon: Camera,
      title: 'Snap Receipts',
      description: 'Take a photo of any receipt. Our AI extracts amount, date, vendor, and tax automatically. Your receipt image is saved and viewable anytime.',
    },
    {
      icon: Type,
      title: 'Type Naturally',
      description: 'Just type "Spent $45 on gas" and watch the AI categorise and log it instantly.',
    },
    {
      icon: Mic,
      title: 'Voice Input',
      description: 'Speak your expenses on the go. Perfect for busy professionals.',
    },
    {
      icon: PieChart,
      title: 'Visual Dashboard',
      description: 'See where your money goes with beautiful charts. Monthly and yearly views.',
    },
    {
      icon: Receipt,
      title: 'Tax Tracking',
      description: 'Automatically separates tax from subtotal. Makes tax time a breeze.',
    },
    {
      icon: HandCoins,
      title: 'Gratuity Tracking',
      description: 'Track tips and gratuities separately. Perfect for business meals and services.',
    },
    {
      icon: FileSpreadsheet,
      title: 'Excel Export',
      description: 'Export by month or year. Ready for your accountant or bookkeeper.',
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Add Your Expense',
      description: 'Snap a receipt, type it out, or just say it. Choose whatever works for you.',
    },
    {
      step: '2',
      title: 'AI Does the Work',
      description: 'Our AI extracts amount, description, category, date, and tax automatically.',
    },
    {
      step: '3',
      title: 'Review & Done',
      description: 'Everything appears in your dashboard. Edit if needed, or just move on.',
    },
  ];

  const comparison = [
    { feature: 'Add expense in seconds', us: true, spreadsheet: false },
    { feature: 'Auto-categorisation', us: true, spreadsheet: false },
    { feature: 'Receipt scanning', us: true, spreadsheet: false },
    { feature: 'Tax automatically separated', us: true, spreadsheet: false },
    { feature: 'Gratuity/tip tracking', us: true, spreadsheet: false },
    { feature: 'Voice input', us: true, spreadsheet: false },
    { feature: 'Visual charts & reports', us: true, spreadsheet: false },
    { feature: 'Export to Excel', us: true, spreadsheet: true },
    { feature: 'Works on any device', us: true, spreadsheet: true },
  ];

  const faqs = [
    {
      question: 'How does the 7-day free trial work?',
      answer: 'You get full access to all Pro features for 7 days. No charge until the trial ends. Cancel anytime before and you won\'t be billed.',
    },
    {
      question: 'What\'s included in the free tier?',
      answer: 'The free tier includes up to 10 expenses, text and voice input, and basic dashboard. Receipt scanning and Excel export require a Pro subscription.',
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel anytime from your account settings. You\'ll continue to have access until the end of your billing period.',
    },
    {
      question: 'How accurate is the AI?',
      answer: 'Our AI is powered by the latest GPT technology and is highly accurate at extracting amounts, dates, and categories. You can always edit if needed.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. All data is encrypted and stored securely. We never share your financial information with third parties.',
    },
    {
      question: 'What currencies are supported?',
      answer: 'ExpenseSnap AI works with any currency. The AI will recognise and extract amounts in your local currency.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo.png" alt="ExpenseSnap AI" className="h-10" />
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                data-testid="dashboard-nav-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="login-nav-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  data-testid="signup-nav-btn"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm mb-8">
            <Zap className="w-4 h-4" />
            AI-Powered Expense Tracking
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 max-w-4xl mx-auto leading-tight">
            Snap receipts.<br />
            Auto-track expenses.<br />
            Export to Excel.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            Stop losing receipts. Stop forgetting expenses. Let AI handle the tedious work 
            so you can focus on running your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 text-lg h-14"
              data-testid="hero-trial-btn"
            >
              Start 7-Day Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/signup')}
              className="border-white/20 hover:bg-white/5 rounded-full px-8 text-lg h-14"
              data-testid="hero-free-btn"
            >
              Get Started Free
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            No credit card required • 10 free expenses • Cancel anytime
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Everything you need to track expenses
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Built for freelancers and small business owners who want to spend less time on bookkeeping.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="bg-card border-white/10 hover:border-primary/30 transition-colors">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sync & Urgency Section */}
      <section className="py-20 border-t border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Sync across devices */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Smartphone className="w-7 h-7 text-primary" />
                </div>
                <ArrowRight className="w-6 h-6 text-muted-foreground" />
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Monitor className="w-7 h-7 text-primary" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3">Scan on your phone, view on your PC</h3>
              <p className="text-muted-foreground">
                Snap a receipt while you're out. By the time you're back at your desk, 
                it's already in your dashboard. Your expenses sync instantly across all your devices.
              </p>
            </div>

            {/* Receipt fading warning */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-amber-500" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3">Scan it before the ink fades</h3>
              <p className="text-muted-foreground">
                We've all been there — you pull out a receipt months later and it's completely blank. 
                Thermal paper fades fast. Snap it now, before it goes in your pocket, and never lose 
                an expense again.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 border-t border-white/10 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              How it works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to never lose track of an expense again.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {howItWorks.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Why switch from spreadsheets?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how ExpenseSnap AI compares to manual tracking.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-3 gap-4 mb-4 text-center">
              <div></div>
              <div className="font-semibold text-primary">ExpenseSnap AI</div>
              <div className="font-semibold text-muted-foreground">Spreadsheets</div>
            </div>
            {comparison.map((row, index) => (
              <div key={index} className="grid grid-cols-3 gap-4 py-3 border-b border-white/10">
                <div className="text-sm">{row.feature}</div>
                <div className="text-center">
                  {row.us ? (
                    <CheckCircle className="w-5 h-5 text-primary mx-auto" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div className="text-center">
                  {row.spreadsheet ? (
                    <CheckCircle className="w-5 h-5 text-muted-foreground mx-auto" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 border-t border-white/10 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start free. Upgrade when you need more.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free Tier */}
            <Card className="bg-card border-white/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">Free</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">$0</span>
                </div>
                <p className="text-muted-foreground mb-6">Perfect for trying it out</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Up to 10 expenses
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Text & voice input
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Basic dashboard
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="w-full border-white/20"
                  onClick={() => navigate('/signup')}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>

            {/* Pro Monthly */}
            <Card className="bg-card border-primary/50 ring-2 ring-primary relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                Most Popular
              </div>
              <CardContent className="pt-8">
                <h3 className="text-xl font-semibold mb-2">Pro Monthly</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">$10</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-muted-foreground mb-6">For active expense tracking</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Unlimited expenses
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Receipt scanning (AI)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Excel export
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Tax tracking
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Gratuity tracking
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    7-day free trial
                  </li>
                </ul>
                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => navigate('/signup')}
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>

            {/* Pro Annual */}
            <Card className="bg-card border-white/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">Pro Annual</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">$100</span>
                  <span className="text-muted-foreground">/year</span>
                </div>
                <p className="text-primary text-sm mb-6">Save $20 per year</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Everything in Pro Monthly
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    2 months free
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    7-day free trial
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="w-full border-white/20"
                  onClick={() => navigate('/signup')}
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Frequently asked questions
            </h2>
          </div>
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card border border-white/10 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Ready to simplify your expense tracking?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Join thousands of freelancers and small business owners who've ditched spreadsheets for good.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 text-lg h-14"
            >
              Start 7-Day Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo.png" alt="ExpenseSnap AI" className="h-8" />
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ExpenseSnap AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
